import { faArrowsRotate } from '@fortawesome/pro-regular-svg-icons';
import { format } from 'date-fns';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../core/components/Alert.tsx';
import Button from '../../core/components/Button.tsx';
import DropArea from '../../core/components/DropArea.tsx';
import FormField from '../../core/components/FormField.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import SegmentedButton from '../../core/components/SegmentedButton.tsx';
import Table, { TableScrollWrapper } from '../../core/components/Table.tsx';
import { DRY_RUN_MAX_UPLOAD_BYTES } from '../constants.ts';
import { fileMatchesPattern, formatFileSize } from '../files.ts';
import useSftpPatternFiles from '../hooks/useSftpPatternFiles.ts';
import { DryRunInputMode } from '../types.ts';

const RefreshIcon = withIcon(faArrowsRotate);

const UPLOAD_ACCEPT = {
    'text/csv': ['.csv'],
    'text/plain': ['.txt'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/json': ['.json'],
    'application/xml': ['.xml'],
    'text/xml': ['.xml'],
};

const MODE_DESCRIPTION_KEYS: Record<DryRunInputMode, string> = {
    PATTERNS: 'importConfig.dryRun.input.modeDescriptions.patterns',
    SFTP: 'importConfig.dryRun.input.modeDescriptions.sftp',
    UPLOAD: 'importConfig.dryRun.input.modeDescriptions.upload',
};

interface PatternsInputPreviewProps {
    patterns: string[];
    importedFileNames: string[];
}

// importedFileNames is ordered newest first, so the first match is the file a regular import picks
const PatternsInputPreview = ({ patterns, importedFileNames }: PatternsInputPreviewProps) => {
    const { t } = useTranslation('masterdata');
    const rows = patterns.map((pattern) => ({
        pattern,
        fileName: importedFileNames.find((name) => fileMatchesPattern(name, pattern)) ?? null,
    }));
    const hasMissingFile = rows.some((row) => !row.fileName);

    return (
        <div className="flex flex-col gap-2">
            <TableScrollWrapper>
                <Table className="w-full table-auto">
                    <Table.Head>
                        <Table.Row>
                            <Table.HeadCell>{t('importConfig.dryRun.input.patterns.pattern')}</Table.HeadCell>
                            <Table.HeadCell>{t('importConfig.dryRun.input.patterns.importedFile')}</Table.HeadCell>
                        </Table.Row>
                    </Table.Head>
                    <Table.Body>
                        {rows.map(({ pattern, fileName }, index) => (
                            <Table.Row key={index}>
                                <Table.Cell>
                                    <span className="font-mono break-all">{pattern}</span>
                                </Table.Cell>
                                <Table.Cell className="whitespace-nowrap">
                                    {fileName ?? (
                                        <span className="text-content-warning-strong">
                                            {t('importConfig.dryRun.input.patterns.noMatch')}
                                        </span>
                                    )}
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </TableScrollWrapper>
            {hasMissingFile && (
                <Alert severity="warning">{t('importConfig.dryRun.input.patterns.missingFileWarning')}</Alert>
            )}
        </div>
    );
};

interface SftpInputPreviewProps {
    sftpPatternFiles: ReturnType<typeof useSftpPatternFiles>;
}

const SftpInputPreview = ({
    sftpPatternFiles: { path, patternFiles, loading, error, refetch },
}: SftpInputPreviewProps) => {
    const { t } = useTranslation('masterdata');

    return (
        <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-content-strong">{t('importConfig.sftp.title')}</span>
                <span className="flex-1 min-w-0 truncate font-mono text-sm text-content-medium">{path}</span>
                <Button
                    variant="ghost"
                    onClick={() => refetch()}
                    aria-label={t('importConfig.sftp.refresh')}
                    title={t('importConfig.sftp.refresh')}
                >
                    <RefreshIcon />
                </Button>
            </div>

            {loading ? (
                <div className="text-sm text-content-medium">{t('importConfig.sftp.loading')}</div>
            ) : error ? (
                <Alert severity="error">{error.message}</Alert>
            ) : (
                <TableScrollWrapper>
                    <Table className="w-full table-auto">
                        <Table.Head>
                            <Table.Row>
                                <Table.HeadCell>{t('importConfig.sftp.pattern')}</Table.HeadCell>
                                <Table.HeadCell>{t('importConfig.sftp.name')}</Table.HeadCell>
                                <Table.HeadCell>{t('importConfig.sftp.size')}</Table.HeadCell>
                                <Table.HeadCell>{t('importConfig.sftp.modified')}</Table.HeadCell>
                            </Table.Row>
                        </Table.Head>
                        <Table.Body>
                            {patternFiles.map(({ pattern, file }, index) => (
                                <Table.Row key={index}>
                                    <Table.Cell>
                                        <span className="font-mono break-all">{pattern}</span>
                                    </Table.Cell>
                                    {file ? (
                                        <>
                                            <Table.Cell className="whitespace-nowrap">{file.name}</Table.Cell>
                                            <Table.Cell className="whitespace-nowrap">
                                                {file.size !== null && formatFileSize(file.size)}
                                            </Table.Cell>
                                            <Table.Cell className="whitespace-nowrap">
                                                {file.modifiedAt && format(new Date(file.modifiedAt), 'PPp')}
                                            </Table.Cell>
                                        </>
                                    ) : (
                                        <Table.Cell colSpan={3} className="text-content-medium">
                                            {t('importConfig.sftp.noFile')}
                                        </Table.Cell>
                                    )}
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                </TableScrollWrapper>
            )}
        </div>
    );
};

interface UploadInputProps {
    patterns: string[];
    fileByPattern: Record<string, File | undefined>;
    onFileChange: (pattern: string, file: File | undefined) => void;
    isTooLarge: boolean;
}

const UploadInput = ({ patterns, fileByPattern, onFileChange, isTooLarge }: UploadInputProps) => {
    const { t } = useTranslation('masterdata');

    return (
        <div className="flex flex-col gap-4">
            {patterns.map((pattern, index) => (
                <FormField
                    key={index}
                    label={pattern}
                    helpText={!fileByPattern[pattern] && t('importConfig.dryRun.input.upload.latestImportedFileUsed')}
                >
                    <DropArea
                        placeholder={t('importConfig.dryRun.input.upload.placeholder')}
                        accept={UPLOAD_ACCEPT}
                        maxFiles={1}
                        files={fileByPattern[pattern] ? [fileByPattern[pattern]] : []}
                        onFilesChange={(files) => onFileChange(pattern, files[0])}
                    />
                </FormField>
            ))}
            {isTooLarge && (
                <Alert severity="error">
                    {t('importConfig.dryRun.input.upload.tooLarge', {
                        maxSize: formatFileSize(DRY_RUN_MAX_UPLOAD_BYTES),
                    })}
                </Alert>
            )}
        </div>
    );
};

interface DryRunInputSelectorProps {
    patterns: string[];
    importedFileNames: string[];
    mode: DryRunInputMode;
    onModeChange: (mode: DryRunInputMode) => void;
    sftpPatternFiles: ReturnType<typeof useSftpPatternFiles>;
    uploadFileByPattern: Record<string, File | undefined>;
    onUploadFileChange: (pattern: string, file: File | undefined) => void;
    isUploadTooLarge: boolean;
}

const DryRunInputSelector = ({
    patterns,
    importedFileNames,
    mode,
    onModeChange,
    sftpPatternFiles,
    uploadFileByPattern,
    onUploadFileChange,
    isUploadTooLarge,
}: DryRunInputSelectorProps) => {
    const { t } = useTranslation('masterdata');
    const hasPatterns = patterns.length > 0;

    return (
        <div className="flex flex-col gap-4">
            <FormField label={t('importConfig.dryRun.input.label')} helpText={t(MODE_DESCRIPTION_KEYS[mode])}>
                <SegmentedButton
                    value={mode}
                    onValueChange={(value) => onModeChange(value as DryRunInputMode)}
                    aria-label={t('importConfig.dryRun.input.label')}
                >
                    <SegmentedButton.Item value="PATTERNS">
                        {t('importConfig.dryRun.input.modes.patterns')}
                    </SegmentedButton.Item>
                    <SegmentedButton.Item value="SFTP">
                        {t('importConfig.dryRun.input.modes.sftp')}
                    </SegmentedButton.Item>
                    <SegmentedButton.Item value="UPLOAD">
                        {t('importConfig.dryRun.input.modes.upload')}
                    </SegmentedButton.Item>
                </SegmentedButton>
            </FormField>

            {!hasPatterns && (
                <div className="text-sm text-content-medium">{t('importConfig.dryRun.input.noPatterns')}</div>
            )}
            {mode === 'PATTERNS' && hasPatterns && (
                <PatternsInputPreview patterns={patterns} importedFileNames={importedFileNames} />
            )}
            {mode === 'SFTP' && hasPatterns && <SftpInputPreview sftpPatternFiles={sftpPatternFiles} />}
            {mode === 'UPLOAD' && hasPatterns && (
                <UploadInput
                    patterns={patterns}
                    fileByPattern={uploadFileByPattern}
                    onFileChange={onUploadFileChange}
                    isTooLarge={isUploadTooLarge}
                />
            )}
        </div>
    );
};

export default DryRunInputSelector;
